import type { MigrationDevice, ReadinessSummary } from '../api/migrations';

// The per-check readiness table the mockups draw (FD-457).
//
// The mockup shows a "Pass Rate" column with a green bar per check. THE PLATFORM
// CANNOT PRODUCE THAT NUMBER, and the difference matters. A device row carries
// only the checks that were FLAGGED — `readinessBlockingReasons` and
// `readinessWarnings`. There is no per-device list of checks that passed, so a
// check absent from both arrays means "not flagged", which is not the same claim
// as "passed": it also covers a check that did not run, or one the deployment's
// policy does not evaluate.
//
// So the third column is labelled **not flagged**. Calling it "passed" would be
// the FD-269 class of defect — a field reporting a state it cannot know.

/**
 * The FD-ADR-027 check codes. Enumerated rather than discovered so the table has
 * a stable row order and a check that has never fired still appears — an
 * operator asking "is BitLocker being checked at all?" gets an answer.
 *
 * Codes NOT in this list still render: `rollupReadiness` appends any unfamiliar
 * code it encounters rather than dropping it, because the ruleset is ATC-hosted
 * (FD-ADR-028) and can legitimately introduce one this build has not been taught.
 */
export const READINESS_CHECKS = [
  'READINESS_FACTS_MISSING',
  'READINESS_FACTS_UNRELIABLE',
  'FREE_DISK',
  'OS_BUILD',
  'OS_SKU',
  'JOIN_SHAPE',
  'ENDPOINT_REACHABILITY',
  'PRIMARY_USER_SID',
  'BITLOCKER',
  'PENDING_REBOOT',
  'INTUNE_MDM_CERT',
  'SERIAL_QUALITY',
  'COLLECTION_ERRORS',
] as const;

export interface CheckRollupRow {
  check: string;
  blocked: number;
  warned: number;
  /** evaluated − blocked − warned. NOT "passed" — see the note above. */
  notFlagged: number;
  /** True when the count came from the server's own aggregate. */
  serverCounted: boolean;
}

export interface ReadinessRollup {
  /** Devices with a stamped verdict. The denominator for every row. */
  evaluated: number;
  rows: CheckRollupRow[];
}

export function rollupReadiness(
  devices: Pick<
    MigrationDevice,
    'readinessStatus' | 'readinessBlockingReasons' | 'readinessWarnings'
  >[],
  summary?: ReadinessSummary | null,
): ReadinessRollup {
  const evaluatedDevices = devices.filter((d) => d.readinessStatus !== null);
  const blocked = new Map<string, number>();
  const warned = new Map<string, number>();

  for (const device of evaluatedDevices) {
    for (const reason of device.readinessBlockingReasons ?? []) {
      blocked.set(reason.check, (blocked.get(reason.check) ?? 0) + 1);
    }
    for (const reason of device.readinessWarnings ?? []) {
      warned.set(reason.check, (warned.get(reason.check) ?? 0) + 1);
    }
  }

  // Any code seen on the wire that this build does not enumerate.
  const extra = [...blocked.keys(), ...warned.keys()]
    .filter((code) => !READINESS_CHECKS.includes(code as (typeof READINESS_CHECKS)[number]))
    .filter((code, index, all) => all.indexOf(code) === index)
    .sort();

  const rows = [...READINESS_CHECKS, ...extra].map((check) => {
    // PREFER THE SERVER'S FIGURE where it exists. `readinessSummary.byBlockingReason`
    // is computed from the same partition read that produced these rows, so when
    // the two disagree the server is right and the grid is a page behind.
    const serverBlocked = summary?.byBlockingReason?.[check];
    const localBlocked = blocked.get(check) ?? 0;
    const blockedCount = typeof serverBlocked === 'number' ? serverBlocked : localBlocked;
    const warnedCount = warned.get(check) ?? 0;
    return {
      check,
      blocked: blockedCount,
      warned: warnedCount,
      notFlagged: Math.max(0, evaluatedDevices.length - blockedCount - warnedCount),
      serverCounted: typeof serverBlocked === 'number',
    };
  });

  return { evaluated: evaluatedDevices.length, rows };
}

/** Human label for a check code. Unknown codes are returned as-is. */
export function checkLabel(code: string): string {
  const map: Record<string, string> = {
    READINESS_FACTS_MISSING: 'Device facts missing',
    READINESS_FACTS_UNRELIABLE: 'Device facts unreliable',
    FREE_DISK: 'Free disk space',
    OS_BUILD: 'OS build',
    OS_SKU: 'OS edition',
    JOIN_SHAPE: 'Join state',
    ENDPOINT_REACHABILITY: 'Endpoint reachability',
    PRIMARY_USER_SID: 'Primary user',
    BITLOCKER: 'BitLocker',
    PENDING_REBOOT: 'Pending reboot',
    INTUNE_MDM_CERT: 'Intune MDM certificate',
    SERIAL_QUALITY: 'Serial number quality',
    COLLECTION_ERRORS: 'Collection errors',
  };
  return map[code] ?? code;
}
